import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import { PeriodType } from '../widget/date-range.widget.model';
import { WidgetService } from '../widget/widget.service';

import {
    CaoDetailsPopup,
    CaoDetailsPopupModelTransformer,
    ExtCaoDetailsPopupItem,
    validateCaoDetailsPopup,
} from './cao-details.widget.model';
import {
    CaoMetricCyclePerformance,
    CaoMetricCyclePerformanceModelTransformer,
    ExtCaoMetricCyclePerformanceItem,
    validateCaoMetricCyclePerformance,
} from './cao-metric-cycle-performance.widget.model';
import {
    CaoOpportunityPopup,
    CaoOpportunityPopupModelTransformer,
    ExtCaoOpportunityPopup,
    validateCaoOpportunityPopup,
} from './cao-opportunity.widget.model';
import {
    CurrentPerformanceChart,
    CurrentPerformanceChartModelTransformer,
    ExtCurrentPerformanceChartMetric,
    validateCurrentPerformanceChart,
} from './current-performance-chart.widget.model';
import {
    DualChartPerformanceComparison,
    DualChartPerformanceComparisonModelTransformer,
    ExtDualChartPerformanceComparisonPillar,
    validateDualChartPerformanceComparison,
} from './dual-chart-performance-comparison.widget.model';
import { createDateOptions } from './legacy-widget.model';
import {
    ExtManagementEffectiveness,
    ManagementEffectiveness,
    ManagementEffectivenessModelTransformer,
    validateManagementEffectiveness,
} from './management-effectiveness.widget.model';
import {
    ExtMbnaCompliancePopupItem,
    MbnaCompliancePopup,
    MbnaCompliancePopupModelTransformer,
    validateMbnaCompliancePopup,
} from './mbna-compliance.widget.model';
import {
    ExtMetricDataListItem,
    MetricDataList,
    MetricDataListModelTransformer,
    validateMetricDataList,
} from './metric-data-list.widget.model';
import {
    ExtMetricFocusPillar,
    MetricFocus,
    MetricFocusModelTransformer,
    validateMetricFocus,
} from './metric-focus.widget.model';
import {
    ExtMi2DetailsPopupItem,
    Mi2DetailsPopup,
    Mi2DetailsPopupModelTransformer,
    validateMi2DetailsPopup,
} from './mi2-details.widget.model';
import {
    ExtMyPerformanceVsPeersItem,
    MyPerformanceVsPeers,
    MyPerformanceVsPeersModelTransformer,
    validateMyPerformanceVsPeers,
} from './my-performance-vs-peers.widget.model';
import {
    ExtPersonalCoachingItem,
    PersonalCoaching,
    PersonalCoachingModelTransformer,
    validatePersonalCoaching,
} from './personal-coaching.widget.model';
import {
    ExtPersonalScorecardChartItem,
    PersonalScorecardChart,
    PersonalScorecardChartModelTransformer,
    validatePersonalScorecardChart,
} from './personal-scorecard-chart.widget.model';
import {
    ExtPersonalScorecardListItem,
    PersonalScorecardList,
    PersonalScorecardListModelTransformer,
    validatePersonalScorecardList,
} from './personal-scorecard-list.widget.model';
import {
    ExtPillarPerformancePopupItem,
    PillarPerformancePopup,
    PillarPerformancePopupModelTransformer,
    validatePillarPerformancePopup,
} from './pillar-performance.widget.model';
import {
    ExtScorecardListPopupItem,
    ScorecardListPopup,
    ScorecardListPopupModelTransformer,
    validateScorecardListPopup,
} from './scorecard-list.widget.model';
import {
    ExtTdbfgComplianceDetailsPopupItem,
    TdbfgComplianceDetailsPopup,
    TdbfgComplianceDetailsPopupModelTransformer,
    validateTdbfgComplianceDetailsPopup,
} from './tdbfg-compliance-details.widget.model';
import {
    ExtTdbfgCompliancePopupItem,
    TdbfgCompliancePopup,
    TdbfgCompliancePopupModelTransformer,
    validateTdbfgCompliancePopup,
} from './tdbfg-compliance.widget.model';
import {
    ExtTeamCoachingPopupItem,
    TeamCoachingPopup,
    TeamCoachingPopupModelTransformer,
    validateTeamCoachingPopup,
} from './team-coaching-details.widget.model';
import {
    ExtTeamCoaching,
    TeamCoaching,
    TeamCoachingModelTransformer,
    TeamCoachingPerformerType,
    validateTeamCoaching,
} from './team-coaching.widget.model';
import {
    ExtTeamDistributionByPillarPillar,
    TeamDistributionByPillar,
    TeamDistributionByPillarModelTransformer,
    validateTeamDistributionByPillar,
} from './team-distribution-by-pillar.widget.model';
import {
    ExtTeamManagementEffectivenessItem,
    TeamManagementEffectiveness,
    TeamManagementEffectivenessModelTransformer,
    validateTeamManagementEffectiveness,
} from './team-management-effectiveness.widget.model';
import {
    ExtTeamScorecardChartItem,
    TeamScorecardChart,
    TeamScorecardChartModelTransformer,
    validateTeamScorecardChart,
} from './team-scorecard-chart.widget.model';
import {
    ExtTopPerformanceOpportunityItem,
    TopPerformanceOpportunity,
    TopPerformanceOpportunityModelTransformer,
    validateTopPerformanceOpportunity,
} from './top-performance-opportunity.widget.model';

@Injectable()
export class LegacyWidgetService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
        private readonly _widgetService: WidgetService,
    ) {}

    currentPerformanceChart(): Observable<CurrentPerformanceChart> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/CurrentPerformanceChart`,
            )
            .pipe(
                map((r: ExtCurrentPerformanceChartMetric[]) =>
                    new CurrentPerformanceChartModelTransformer().transform(r),
                ),
                tap((model: CurrentPerformanceChart) =>
                    validateCurrentPerformanceChart(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('CurrentPerformanceChart failed');
                }),
            );
    }

    dualChartPerformanceComparison(
        widgetId: number,
    ): Observable<DualChartPerformanceComparison[]> {
        if (!this._widgetService.dateRange.value) {
            return of<DualChartPerformanceComparison[]>(null);
        }

        const body = createDateOptions(
            this._widgetService.dateRange.value.eventDate,
            this._widgetService.dateRange.value.type,
            widgetId,
        );

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/DualChartPerformanceComparison`,
                body,
            )
            .pipe(
                map((r: ExtDualChartPerformanceComparisonPillar[]) =>
                    new DualChartPerformanceComparisonModelTransformer().transform(
                        r,
                    ),
                ),
                tap((model: DualChartPerformanceComparison[]) =>
                    validateDualChartPerformanceComparison(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('DualChartPerformanceComparison failed');
                }),
            );
    }

    mbnaDualChartPerformanceComparison(
        widgetId: number,
        populationType: number,
    ): Observable<DualChartPerformanceComparison[]> {
        const body = createDateOptions(
            this._widgetService.dateRangeCompliance.value.eventDate,
            this._widgetService.dateRangeCompliance.value.type,
            widgetId,
        );
        const params = new HttpParams().set(
            'populationType',
            populationType.toString(),
        );
        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/MbnaDualChartPerformanceComparison`,
                body,
                { params },
            )
            .pipe(
                map((r: ExtDualChartPerformanceComparisonPillar[]) =>
                    new DualChartPerformanceComparisonModelTransformer().transform(
                        r,
                    ),
                ),
                tap((model: DualChartPerformanceComparison[]) =>
                    validateDualChartPerformanceComparison(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('DualChartPerformanceComparison failed');
                }),
            );
    }

    topPerformanceOpportunity(
        isPerformance: boolean,
    ): Observable<TopPerformanceOpportunity> {
        const params = new HttpParams().set(
            'isPerformance',
            isPerformance ? 'true' : 'false',
        );

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/TopPerformanceOpportunity`,
                { params },
            )
            .pipe(
                map((r: ExtTopPerformanceOpportunityItem[]) =>
                    new TopPerformanceOpportunityModelTransformer().transform(
                        r,
                    ),
                ),
                tap((model: TopPerformanceOpportunity) =>
                    validateTopPerformanceOpportunity(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('TopPerformanceOpportunity failed');
                }),
            );
    }

    teamDistributionByPillar(): Observable<TeamDistributionByPillar> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/TeamDistributionByPillar`,
            )
            .pipe(
                map((r: ExtTeamDistributionByPillarPillar[]) =>
                    new TeamDistributionByPillarModelTransformer().transform(r),
                ),
                tap((model: TeamDistributionByPillar) =>
                    validateTeamDistributionByPillar(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('TeamDistributionByPillar failed');
                }),
            );
    }

    metricFocus(widgetId: number): Observable<MetricFocus[]> {
        if (!this._widgetService.dateRange.value) {
            return of<MetricFocus[]>(null);
        }

        const body = createDateOptions(
            this._widgetService.dateRange.value.eventDate,
            this._widgetService.dateRange.value.type,
            widgetId,
        );

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/MetricFocus`,
                body,
            )
            .pipe(
                map((r: ExtMetricFocusPillar[]) =>
                    new MetricFocusModelTransformer().transform(r),
                ),
                tap((model: MetricFocus[]) => validateMetricFocus(model)),
                catchError((error) => {
                    console.error(error);
                    throw new Error('MetricFocus failed');
                }),
            );
    }

    metricDataList(widgetId: number): Observable<MetricDataList> {
        if (!this._widgetService.dateRange.value) {
            return of<MetricDataList>(null);
        }

        const body = createDateOptions(
            this._widgetService.dateRange.value.eventDate,
            this._widgetService.dateRange.value.type,
            widgetId,
        );

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/MetricDataList`,
                body,
            )
            .pipe(
                map((r: ExtMetricDataListItem[]) =>
                    new MetricDataListModelTransformer().transform(r),
                ),
                tap((model: MetricDataList) => validateMetricDataList(model)),
                catchError((error) => {
                    console.error(error);
                    throw new Error('MetricDataList failed');
                }),
            );
    }

    teamScorecardChart(): Observable<TeamScorecardChart> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/TeamScoreCardChart`,
            )
            .pipe(
                map((r: ExtTeamScorecardChartItem[]) =>
                    new TeamScorecardChartModelTransformer().transform(r),
                ),
                tap((model: TeamScorecardChart) =>
                    validateTeamScorecardChart(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('TeamScoreCardChart failed');
                }),
            );
    }

    personalScorecardList(): Observable<PersonalScorecardList> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/PersonalScoreCardList`,
            )
            .pipe(
                map((r: ExtPersonalScorecardListItem[]) =>
                    new PersonalScorecardListModelTransformer().transform(r),
                ),
                tap((model: PersonalScorecardList) =>
                    validatePersonalScorecardList(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('PersonalScoreCardList failed');
                }),
            );
    }

    personalScorecardChart(): Observable<PersonalScorecardChart> {
        const params = new HttpParams().set('superGroupId', '0');

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/PersonalScoreCardChart`,
                { params },
            )
            .pipe(
                map((r: ExtPersonalScorecardChartItem[]) =>
                    new PersonalScorecardChartModelTransformer().transform(r),
                ),
                tap((model: PersonalScorecardChart) =>
                    validatePersonalScorecardChart(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('PersonalScoreCardChart failed');
                }),
            );
    }

    personalCoaching(): Observable<PersonalCoaching> {
        const params = new HttpParams().set('filterTypeId', '1');

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/PersonalCoaching`,
                { params },
            )
            .pipe(
                map((r: ExtPersonalCoachingItem[]) =>
                    new PersonalCoachingModelTransformer().transform(r),
                ),
                tap((model: PersonalCoaching) =>
                    validatePersonalCoaching(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('PersonalCoaching failed');
                }),
            );
    }

    teamCoaching(): Observable<TeamCoaching> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/TeamCoaching`,
            )
            .pipe(
                map((r: ExtTeamCoaching[]) =>
                    new TeamCoachingModelTransformer().transform(r[0]),
                ),
                tap((model: TeamCoaching) => validateTeamCoaching(model)),
                catchError((error) => {
                    console.error(error);
                    throw new Error('TeamCoaching failed');
                }),
            );
    }

    managementEffectiveness(): Observable<ManagementEffectiveness> {
        const params = new HttpParams().set('use30DayRolling', 'false');

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/ManagementEffectiveness`,
                { params },
            )
            .pipe(
                map((r: ExtManagementEffectiveness) =>
                    new ManagementEffectivenessModelTransformer().transform(r),
                ),
                tap((model: ManagementEffectiveness) =>
                    validateManagementEffectiveness(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('ManagementEffectiveness failed');
                }),
            );
    }

    teamManagementEffectiveness(): Observable<TeamManagementEffectiveness> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/TeamManagementEffectiveness`,
            )
            .pipe(
                map((r: ExtTeamManagementEffectivenessItem[]) =>
                    new TeamManagementEffectivenessModelTransformer().transform(
                        r,
                    ),
                ),
                tap((model: TeamManagementEffectiveness) =>
                    validateTeamManagementEffectiveness(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('TeamManagementEffectiveness failed');
                }),
            );
    }

    myPerformanceVsPeers(): Observable<MyPerformanceVsPeers> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/MyPerformanceVsPeers`,
            )
            .pipe(
                map((r: ExtMyPerformanceVsPeersItem[]) =>
                    new MyPerformanceVsPeersModelTransformer().transform(r),
                ),
                tap((model: MyPerformanceVsPeers) =>
                    validateMyPerformanceVsPeers(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('MyPerformanceVsPeers failed');
                }),
            );
    }

    mi2DetailsPopup(): Observable<Mi2DetailsPopup> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/GetMI2DetailsPopup`,
            )
            .pipe(
                map((r: ExtMi2DetailsPopupItem[]) =>
                    new Mi2DetailsPopupModelTransformer().transform(r),
                ),
                tap((model: Mi2DetailsPopup) => validateMi2DetailsPopup(model)),
                catchError((error) => {
                    console.error(error);
                    throw new Error('GetMI2DetailsPopup failed');
                }),
            );
    }

    caoOpportunityPopup(userId: number): Observable<CaoOpportunityPopup> {
        const params = new HttpParams().set('userId', userId.toString());

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/GetCAOOpportunityPopup`,
                { params },
            )
            .pipe(
                map((r: ExtCaoOpportunityPopup) =>
                    new CaoOpportunityPopupModelTransformer().transform(r),
                ),
                tap((model: CaoOpportunityPopup) =>
                    validateCaoOpportunityPopup(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('GetCAOOpportunityPopup failed');
                }),
            );
    }

    caoDetailsPopup(
        managerId: number,
        userId: number,
    ): Observable<CaoDetailsPopup> {
        const params = new HttpParams()
            .set('managerId', managerId.toString())
            .set('userId', userId.toString());

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/GetCAODetailsPopup`,
                { params },
            )
            .pipe(
                map((r: ExtCaoDetailsPopupItem[]) =>
                    new CaoDetailsPopupModelTransformer().transform(r),
                ),
                tap((model: CaoDetailsPopup) => validateCaoDetailsPopup(model)),
                catchError((error) => {
                    console.error(error);
                    throw new Error('GetCAODetailsPopup failed');
                }),
            );
    }

    caoMetricCyclePerformance(
        userId: number,
        metricId: number,
    ): Observable<CaoMetricCyclePerformance> {
        const params = new HttpParams()
            .set('userId', userId.toString())
            .set('metricId', metricId.toString());

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/GetCAOMetricCyclePerformance`,
                { params },
            )
            .pipe(
                map((r: ExtCaoMetricCyclePerformanceItem[]) =>
                    new CaoMetricCyclePerformanceModelTransformer().transform(
                        r,
                    ),
                ),
                tap((model: CaoMetricCyclePerformance) =>
                    validateCaoMetricCyclePerformance(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('GetCAOMetricCyclePerformance failed');
                }),
            );
    }

    // TODO revise
    teamCoachingPopup(
        performerType: TeamCoachingPerformerType,
    ): Observable<TeamCoachingPopup | PersonalCoaching> {
        const params = new HttpParams().set(
            'filterTypeId',
            (performerType + 1).toString(),
        );

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/GetTeamCoachingPopup`,
                { params },
            )
            .pipe(
                map(
                    (
                        model: Array<
                            ExtTeamCoachingPopupItem | ExtPersonalCoachingItem
                        >,
                    ) => {
                        return performerType ===
                            TeamCoachingPerformerType.WITHOUT_GOALS
                            ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                              new TeamCoachingPopupModelTransformer().transform(
                                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                                  <ExtTeamCoachingPopupItem[]>model,
                              )
                            : // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                              new PersonalCoachingModelTransformer().transform(
                                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                                  <ExtPersonalCoachingItem[]>model,
                              );
                    },
                ),
                tap((model: TeamCoachingPopup | PersonalCoaching) => {
                    return performerType ===
                        TeamCoachingPerformerType.WITHOUT_GOALS
                        ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                          validateTeamCoachingPopup(<TeamCoachingPopup>model)
                        : // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                          validatePersonalCoaching(<PersonalCoaching>model);
                }),
                catchError((error) => {
                    console.error(error);
                    throw new Error('GetTeamCoachingPopup failed');
                }),
            );
    }

    scorecardListPopup(startDate: Date): Observable<ScorecardListPopup> {
        const params = new HttpParams().set('startDate', startDate.toString());

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/GetScorecardListPopup`,
                { params },
            )
            .pipe(
                map((r: ExtScorecardListPopupItem[]) =>
                    new ScorecardListPopupModelTransformer().transform(r),
                ),
                tap((model: ScorecardListPopup) =>
                    validateScorecardListPopup(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('GetScorecardListPopup failed');
                }),
            );
    }

    pillarPerformancePopup(
        widgetId: number,
    ): Observable<PillarPerformancePopup> {
        if (!this._widgetService.dateRange.value) {
            return of<PillarPerformancePopup>(null);
        }

        const body = createDateOptions(
            this._widgetService.dateRange.value.eventDate,
            this._widgetService.dateRange.value.type,
            widgetId,
        );

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/GetPillarPerformancePopup`,
                body,
            )
            .pipe(
                map((r: ExtPillarPerformancePopupItem[]) =>
                    new PillarPerformancePopupModelTransformer().transform(r),
                ),
                tap((model: PillarPerformancePopup) =>
                    validatePillarPerformancePopup(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('GetPillarPerformancePopup failed');
                }),
            );
    }

    mbnaCompliancePopup(
        widgetId: number,
        populationType: number,
        userId?: number,
    ): Observable<MbnaCompliancePopup> {
        if (!this._widgetService.dateRangeCompliance.value) {
            return of<MbnaCompliancePopup>(null);
        }

        let params = new HttpParams()
            .set(
                'periodType',
                this._widgetService.dateRangeCompliance.value.type.toString(),
            )
            .set('populationType', populationType.toString());
        if (userId != null) {
            params = params.append('userId', userId.toString());
        }
        const body = createDateOptions(
            this._widgetService.dateRangeCompliance.value.eventDate,
            this._widgetService.dateRangeCompliance.value.type,
            widgetId,
        );

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/GetMbnaCompliancePopup`,
                body,
                { params },
            )
            .pipe(
                map((r: ExtMbnaCompliancePopupItem[]) =>
                    new MbnaCompliancePopupModelTransformer().transform(r),
                ),
                tap((model: MbnaCompliancePopup) =>
                    validateMbnaCompliancePopup(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('GetMbnaCompliancePopup failed');
                }),
            );
    }

    mbnaComplianceDetailsPopup(
        userId: number,
        populationId: number,
        widgetId: number,
        populationType: number,
    ): Observable<TdbfgComplianceDetailsPopup> {
        if (!this._widgetService.dateRangeCompliance.value) {
            return of<TdbfgComplianceDetailsPopup>(null);
        }

        const params = new HttpParams()
            .set('userId', userId.toString())
            .set('populationId', populationId.toString())
            .set(
                'periodType',
                this._widgetService.dateRangeCompliance.value.type.toString(),
            )
            .set('populationType', populationType.toString());
        const body = createDateOptions(
            this._widgetService.dateRange.value.eventDate,
            this._widgetService.dateRange.value.type,
            widgetId,
        );

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/GetMbnaComplianceDetailsPopup`,
                body,
                { params },
            )
            .pipe(
                map((r: ExtTdbfgComplianceDetailsPopupItem[]) =>
                    new TdbfgComplianceDetailsPopupModelTransformer().transform(
                        r,
                    ),
                ),
                tap((model: TdbfgComplianceDetailsPopup) =>
                    validateTdbfgComplianceDetailsPopup(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('GetTDBFGComplianceDetailsPopup failed');
                }),
            );
    }

    tdbfgCompliancePopup(widgetId: number): Observable<TdbfgCompliancePopup> {
        if (!this._widgetService.dateRange.value) {
            return of<TdbfgCompliancePopup>(null);
        }

        const params = new HttpParams().set(
            'isQtr',
            this._widgetService.dateRange.value.type === PeriodType.QUARTER
                ? 'true'
                : 'false',
        );

        const body = createDateOptions(
            this._widgetService.dateRange.value.eventDate,
            this._widgetService.dateRange.value.type,
            widgetId,
        );

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/GetTDBFGCompliancePopup`,
                body,
                { params },
            )
            .pipe(
                map((r: ExtTdbfgCompliancePopupItem[]) =>
                    new TdbfgCompliancePopupModelTransformer().transform(r),
                ),
                tap((model: TdbfgCompliancePopup) =>
                    validateTdbfgCompliancePopup(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('GetTDBFGCompliancePopup failed');
                }),
            );
    }

    tdbfgComplianceDetailsPopup(
        userId: number,
        superGroupId: number,
        widgetId: number,
    ): Observable<TdbfgComplianceDetailsPopup> {
        if (!this._widgetService.dateRange.value) {
            return of<TdbfgComplianceDetailsPopup>(null);
        }

        const params = new HttpParams()
            .set('userId', userId.toString())
            .set('supergroupId', superGroupId.toString())
            .set(
                'isQtr',
                this._widgetService.dateRange.value.type === PeriodType.QUARTER
                    ? 'true'
                    : 'false',
            );

        const body = createDateOptions(
            this._widgetService.dateRange.value.eventDate,
            this._widgetService.dateRange.value.type,
            widgetId,
        );

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.WIDGET_DATA}/widgetdata/GetTDBFGComplianceDetailsPopup`,
                body,
                { params },
            )
            .pipe(
                map((r: ExtTdbfgComplianceDetailsPopupItem[]) =>
                    new TdbfgComplianceDetailsPopupModelTransformer().transform(
                        r,
                    ),
                ),
                tap((model: TdbfgComplianceDetailsPopup) =>
                    validateTdbfgComplianceDetailsPopup(model),
                ),
                catchError((error) => {
                    console.error(error);
                    throw new Error('GetTDBFGComplianceDetailsPopup failed');
                }),
            );
    }
}
